import { useCallback } from 'react'

import { useGetUserSettingQuery, useUpdateUserSettingMutation } from '../../../api/combined'
import { UserSettingKeys, UserSettings } from '../../account/types/UserSettings'
import { TrackedGame } from '../types/TrackedGame'

export const useLiveEventsSelectedGameIdsSetting = () => {
  const query = useGetUserSettingQuery(UserSettingKeys.liveEventsTrackerSelectedGameIds)

  return {
    ...query,
    data: query.data as UserSettings[UserSettingKeys.liveEventsTrackerSelectedGameIds],
  }
}

export const useUpdateLiveEventsSelectedGameIdsSetting = () => {
  const [updateUserSetting, query] = useUpdateUserSettingMutation()

  // The trigger reference must stay stable across the mutation's idle → pending → fulfilled status
  // transitions. RTK Query returns a fresh `query` object on every transition, so the previous
  // useMemo([query, updateUserSetting]) produced a new trigger on every mutation. Consumers that
  // list this trigger in a useEffect dependency array — e.g. LiveEventsTrackerPage's "keep URL games
  // in user settings" effect — would then re-run mid-mutation, while the optimistic cache already
  // held the cleared value but the URL still held the pre-clear ids, and re-persist the just-cleared
  // ids back to the setting. RTK Query's mutation trigger is itself referentially stable, so
  // useCallback([updateUserSetting]) keeps our wrapper stable and the consumer effect only re-fires
  // on genuine URL changes. (GR-2870)
  const updateLiveEventsSelectedGameIdsSetting = useCallback(
    (trackedGames: TrackedGame[]) =>
      updateUserSetting({
        settingKey: UserSettingKeys.liveEventsTrackerSelectedGameIds,
        value: trackedGames.map((trackedGame) => trackedGame.game.id),
      }),
    [updateUserSetting]
  )

  return {
    updateLiveEventsSelectedGameIdsSetting,
    ...query,
  }
}
